import { AfterViewInit, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { applicationMessages, toasterMessages } from '@messages/*';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { LoaderService } from 'src/app/services/loader.service';
import { CreateUpdateCodingQuesComponent } from '../common/create-update-coding-ques/create-update-coding-ques.component';
import { CreateUpdateMcqComponent } from '../common/create-update-mcq/create-update-mcq.component';
import { CreateUpdateOeqComponent } from '../common/create-update-oeq/create-update-oeq.component';
import { DashboardService } from '../dashboard.service';
import { CqsListComponent } from '../common/cqs-list/cqs-list.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-questions',
  templateUrl: './questions.component.html',
  styleUrls: ['./questions.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class QuestionsComponent implements OnInit, AfterViewInit {

  @ViewChild('questionsTabGroup') tabGroup;
  @ViewChild('cqsList') cqsList: CqsListComponent;
  mcqsInfo = {
    questions: [],
    type: 'MULTIPLE_CHOICE',
    category: {
      title: 'Multiple Choice Questions',
      count: '0',
      description: 'Previously Generated Questions for the assessment',
      category: 'PREVIOUSLYGENERATED'
    },
    totalQuestions: 0,
    assessmentId: '',
    from: 'questionsList'
  }

  oeqsInfo = {
    questions: [],
    type: 'OPEN_ENDED',
    category: {
      title: 'Open Ended Questions',
      count: '0',
      description: 'Previously Generated Questions for the assessment',
      category: 'PREVIOUSLYGENERATED'
    },
    totalQuestions: 0,
    assessmentId: '',
    from: 'questionsList'
  }

  cqsInfo = {
    questions: [],
    type: 'CODING',
    category: {
      title: 'Coding Questions',
      count: '0',
      description: 'Previously Generated Questions for the assessment',
      category: 'PREVIOUSLYGENERATED'
    },
    totalQuestions: 0,
    assessmentId: '',
    from: 'questionsList'
  }


  filterValues: any = {};
  codingQuestions: any = [];
  totalMcqQuestions: number = 0;
  totalOEQuestions: number = 0;
  totalCodingQuestions: number = 0;

  private mcqPageNo: number = 1;
  private oePageNo: number = 1;
  private cqPageNo: number = 1;
  public activeTabIndex = 0;
  public questionId: string = '';
  private subscriptions: Subscription[] = [];

  // Separate loading states for each question type
  isLoadingMoreMcqs = false;
  isLoadingMoreOeqs = false;
  isLoadingMoreCqs = false;

  public allExpanded = false;
  public showMcqBulkCheckboxes = false;
  public showOeqBulkCheckboxes = false;
  public showCqBulkCheckboxes = false;

  // Feature flags
  public enableCreateCodingQuestions = environment.ENABLE_CREATE_CODING_QUESTIONS === true;

  constructor(
    public dialog: MatDialog,
    private dashboardService: DashboardService,
    private toastr: ToastrService,
    private loaderService: LoaderService) {
  }

  ngOnInit() {
    this.getMCQuestions(1, 'MULTIPLE_CHOICE', this.filterValues);
    this.getOEQuestions(1, 'OPEN_ENDED', this.filterValues);
    this.getCodingQuestions(1, 'CODING', this.filterValues);
  }

  ngAfterViewInit() {
    this.activeTabIndex = this.tabGroup.selectedIndex;
  }

  onApplyFilters(requestObj: any): void {
    this.filterValues = requestObj;
    if (this.activeTabIndex === 0) {
      this.mcqPageNo = 1;
      this.mcqsInfo.questions = [];
      this.getMCQuestions(1, 'MULTIPLE_CHOICE', this.filterValues);
    } else if (this.activeTabIndex === 1) {
      this.oePageNo = 1
      this.oeqsInfo.questions = [];
      this.getOEQuestions(1, 'OPEN_ENDED', this.filterValues);
    } else if (this.activeTabIndex === 2) {
      this.cqPageNo = 1
      this.cqsInfo.questions = [];
      this.getCodingQuestions(1, 'CODING', this.filterValues);
    }
  }

  onClearFilters(): void {
    // Reset filter values and reload questions as needed
    this.filterValues = {};
    this.onApplyFilters(this.filterValues);
  }

  /**
   * Handle search term from the filter component
   * @param searchTerm - The search term to filter questions by question text
   */
  onSearch(searchTerm: string): void {
    // Update filter values with search term and reload questions
    this.filterValues.searchTerm = searchTerm;
    this.onApplyFilters(this.filterValues);
  }

  /**
   * Extract total count from API response supporting multiple shapes
   */
  private extractTotal(response: any): number {
    if (!response) { return 0; }
    const total = (response as any).total ?? (response as any).totalDocs ?? (response as any).totalCount ?? (Array.isArray((response as any).docs) ? (response as any).docs.length : 0);
    return Number.isFinite(total) ? total : 0;
  }

  /**
   * Generate headers for API requests
   * @param page Current page number
   * @returns Headers object or undefined
   */
  private getHeaders(page: number): any {
    return page > 1 ? { 'skip-loader': 'true' } : undefined;
  }

  /**
   * Normalize payload for previously generated questions: use topics instead of subTopics
   */
  private normalizeQuestionFilters(req: any, info: any): any {
    if (info?.category?.category === 'PREVIOUSLYGENERATED' && Array.isArray(req?.subTopics) && req.subTopics.length > 0) {
      // Only remap when subTopics actually contains values and topics are not provided
      const { subTopics, topics, ...rest } = req;
      if (!topics || topics.length === 0) {
        return { ...rest, topics: subTopics };
      }
      return { ...rest, topics };
    }
    return req;
  }

  /** get multiple choice questions list */
  private getMCQuestions(page, type, req) {
    if (page === 1) {
      this.loaderService.show('Loading multiple choice questions');
    } else {
      this.isLoadingMoreMcqs = true;
    }

    const normalizedReq = this.normalizeQuestionFilters(req, this.mcqsInfo);

    this.subscriptions.push(
      this.dashboardService.getQuestions(page, type, normalizedReq, this.getHeaders(page)).subscribe((response: any) => {
        if (page === 1) {
          this.loaderService.hide();
        } else {
          this.isLoadingMoreMcqs = false;
        }
        // Append or set questions as needed
        if (page === 1) {
          this.mcqsInfo.questions = response.docs;
        } else {
          this.mcqsInfo.questions = [...this.mcqsInfo.questions, ...response.docs];
        }
        const total = this.extractTotal(response);
        this.totalMcqQuestions = total;
        this.mcqsInfo.totalQuestions = total;
      }, (err) => {
        if (page === 1) {
          this.loaderService.hide();
        } else {
          this.isLoadingMoreMcqs = false;
        }
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
      })
    );
  }

  /** Get Open ended questions list */
  private getOEQuestions(page, type, req) {
    if (page === 1) {
      this.loaderService.show('Loading open ended questions');
    } else {
      this.isLoadingMoreOeqs = true;
    }

    const normalizedReq = this.normalizeQuestionFilters(req, this.oeqsInfo);

    this.subscriptions.push(
      this.dashboardService.getQuestions(page, type, normalizedReq, this.getHeaders(page)).subscribe((response: any) => {
        if (page === 1) {
          this.loaderService.hide();
        } else {
          this.isLoadingMoreOeqs = false;
        }
        if (page === 1) {
          this.oeqsInfo.questions = response.docs;
        } else {
          this.oeqsInfo.questions = [...this.oeqsInfo.questions, ...response.docs];
        }
        const total = this.extractTotal(response);
        this.totalOEQuestions = total;
        this.oeqsInfo.totalQuestions = total;
      }, (err) => {
        if (page === 1) {
          this.loaderService.hide();
        } else {
          this.isLoadingMoreOeqs = false;
        }
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
      })
    );
  }

  /**Get Coding Questions */
  private getCodingQuestions(page, type, req) {
    if (page === 1) {
      this.loaderService.show('Loading coding questions');
    } else {
      this.isLoadingMoreCqs = true;
    }

    const normalizedReq = this.normalizeQuestionFilters(req, this.cqsInfo);

    this.subscriptions.push(
      this.dashboardService.getQuestions(page, type, normalizedReq, this.getHeaders(page)).subscribe((response: any) => {
        if (page === 1) {
          this.loaderService.hide();
        } else {
          this.isLoadingMoreCqs = false;
        }
        if (page === 1) {
          this.cqsInfo.questions = response.docs;
        } else {
          this.cqsInfo.questions = [...this.cqsInfo.questions, ...response.docs];
        }
        const total = this.extractTotal(response);
        this.totalCodingQuestions = total;
        this.cqsInfo.totalQuestions = total;
      }, (err) => {
        if (page === 1) {
          this.loaderService.hide();
        } else {
          this.isLoadingMoreCqs = false;
        }
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
      })
    );
  }

  /** Delete multiple choice question */
  deleteMulChoiceQuestion(id) {
    if (id) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'delete-dialog-container',
        data: { danger: true, messageBody: applicationMessages.questionDeleteConfirm }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'confirm') {
          this.dashboardService.deleteQuestion(id).subscribe((response: any) => {
            const index = this.mcqsInfo?.questions?.findIndex(x => x._id === id);
            this.mcqsInfo?.questions?.splice(index, 1);
            this.totalMcqQuestions -= 1;
            this.mcqsInfo.totalQuestions -= 1;
            this.toastr.success(toasterMessages.questionMCQDeleteSuccess, 'Question deleted', { timeOut: 2500 });
          }, err => {
            this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
          });
        }
      });
    }
  }

  /** Delete open ended question */
  deleteOpenEndedQuestion(id) {
    if (id) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'delete-dialog-container',
        data: { danger: true, messageBody: applicationMessages.questionDeleteConfirm }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'confirm') {
          this.dashboardService.deleteQuestion(id).subscribe((response: any) => {
            const index = this.oeqsInfo?.questions?.findIndex(x => x._id === id);
            this.oeqsInfo?.questions?.splice(index, 1);
            this.totalOEQuestions -= 1;
            this.oeqsInfo.totalQuestions -= 1;
            this.toastr.success(toasterMessages.questionOEQDeleteSuccess, 'Question deleted', { timeOut: 2500 });
          }, err => {
            this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
          });
        }
      });
    }
  }

  /** Delete Coding question */
  deleteCodingQuestion(id) {
    if (id) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'delete-dialog-container',
        data: { danger: true, messageBody: applicationMessages.questionDeleteConfirm }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'confirm') {
          this.dashboardService.deleteQuestion(id).subscribe((response: any) => {
            const index = this.cqsInfo?.questions.findIndex(x => x._id === response._id);
            this.cqsInfo?.questions.splice(index, 1);
            this.totalCodingQuestions -= 1;
            this.cqsInfo.totalQuestions -= 1;
            this.toastr.info(toasterMessages.questionCQDeleteSuccess, 'Question deleted', { timeOut: 2500 });
          }, err => {
            this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
          });
        }
      });
    }
  }

  /** Create a copy of multiple choice Question */
  copyMcq(question): void {
    this.copyMulChoiceQuestion({ id: question._id });
  }

  /** Create or Update multiple choice Question */
  createOrUpdateMcq(obj = null) {
    const dialogRef = this.dialog.open(CreateUpdateMcqComponent, {
      width: '700px',
      disableClose: false,
      autoFocus: true,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      panelClass: 'create-update-mcq-dialog',
      data: {
        update: obj ? true : false,
        content: obj ? obj : ``,
        difficultyLevel: 'MEDIUM'
      }
    });
    dialogRef.componentInstance.createUpdateMcq.subscribe((question: any) => {
      const mcqReqObj = {
        questionType: 'MULTIPLE_CHOICE',
        questionText: question?.questionText,
        answer: question?.enterpriseOptions[question.answerIndex],
        enterpriseOptions: question?.enterpriseOptions,
        topicRefs: question?.topics,
        difficultyLevel: question?.difficultyLevel,
        hourLimit: question?.hourLimit,
        minLimit: question?.minLimit,
        subTopics: question?.subTopics,
        _id: question?._id,
        shuffle: question?.shuffle ?? true
      };
      if (question?.update) {
        this.updateMulChoiceQuestion(mcqReqObj, dialogRef);
      } else {
        this.createMulChoiceQuestion(mcqReqObj, dialogRef);
      }
    });
  }

  /** create multiple choice question request */
  private createMulChoiceQuestion(question, dialogRef = null) {
    this.loaderService.show('Adding multiple choice question');
    this.subscriptions.push(
      this.dashboardService.createQuestion(question).subscribe((response) => {
        this.mcqsInfo?.questions?.unshift(response);
        this.totalMcqQuestions += 1;
        this.mcqsInfo.totalQuestions += 1;
        this.toastr.success(toasterMessages.questionMCQAdded, 'Question added', { timeOut: 2500 });
        dialogRef?.close();
        this.loaderService.hide();
      }, (err) => {
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
        this.loaderService.hide();
      })
    );
  }

  /** create copy of multiple choice question request */
  private copyMulChoiceQuestion(question) {
    this.loaderService.show('Copying multiple choice question');
    this.subscriptions.push(
      this.dashboardService.CopyQuestion(question).subscribe((response) => {
        this.mcqsInfo?.questions?.unshift(response);
        this.totalMcqQuestions += 1;
        this.mcqsInfo.totalQuestions += 1;
        this.toastr.success(toasterMessages.questionMCQAdded, 'Question added', { timeOut: 2500 });
        this.loaderService.hide();
      }, (err) => {
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
        this.loaderService.hide();
      })
    );
  }

  /** Update multiple choice question api request */
  private updateMulChoiceQuestion(question, dialogRef) {
    this.loaderService.show('Updating multiple choice question');
    this.subscriptions.push(
      this.dashboardService.updateQuestion(question, question._id).subscribe((response) => {
        dialogRef.close();
        const selectedIndex = this.mcqsInfo?.questions?.findIndex(x => x._id === question._id);
        this.mcqsInfo.questions[selectedIndex] = response;
        this.loaderService.hide();
        this.toastr.success(toasterMessages.questionMCQUpdated, 'Question updated', { timeOut: 2500 });
      }, err => {
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
        this.loaderService.hide();
      })
    );
  }

  /** Create copy of coding question */
  copyCQ(question) {
    this.copyCodingQuestion({ id: question._id });
  }

  /** Create update coding question */
  createOrUpdateCQ(obj = null) {
    if (!this.enableCreateCodingQuestions) {
      // Guard: creation disabled for this release
      this.toastr.info('Coding questions creation is coming soon.', 'Coming soon', { timeOut: 2500 });
      return;
    }
    const dialogRef = this.dialog.open(CreateUpdateCodingQuesComponent, {
      width: '80%',
      panelClass: 'create-update-coding-dialog',
      data: {
        update: obj ? true : false,
        content: obj ? obj : ``,
        difficultyLevel: 'MEDIUM'
      }
    });
    dialogRef.componentInstance.createUpdateCQ.subscribe((question: any) => {
      question.functionSignature.language_id = question?.functionSignature?.language_id;
      question.functionSignature.source_code = question?.answer;
      // Ensure initial code is sent inside functionSignature for backend to update
      if (question?.initialCode) {
        question.functionSignature.initial_code = question.initialCode;
      }
      const cqReqObj = {
        ...question
      };
      if (question?.update || this.questionId) {
        cqReqObj._id = cqReqObj._id || this.questionId;
        this.updateCodingQuestion(cqReqObj, dialogRef);
      } else {
        this.createCodingQuestion(cqReqObj, dialogRef);
      }
    });
    dialogRef.afterClosed().subscribe(result => { })
  }

  /** Create coding question request */
  private createCodingQuestion(question, dialogRef) {
    this.loaderService.show('Adding coding question');
    this.dashboardService.createQuestion(question).subscribe((response) => {
      this.questionId = response._id;
      if (dialogRef) {
        dialogRef.componentInstance.questionResponse = response;
        dialogRef.componentInstance.data.update = true;
        dialogRef.componentInstance.editObj = response;
        dialogRef.componentInstance.isNewLangQuestion = false;
      }
      // Reflect in UI list and counters
      this.cqsInfo?.questions?.unshift(response);
      this.totalCodingQuestions += 1;
      if (typeof this.cqsInfo.totalQuestions === 'number') {
        this.cqsInfo.totalQuestions += 1;
      }
      this.toastr.success(toasterMessages.questionCQAdded, 'Question added', { timeOut: 2500 });
      this.loaderService.hide();
    }, (err) => {
      this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
      this.loaderService.hide();
    });
  }

  /** Create Copy of coding question request */
  private copyCodingQuestion(question) {
    this.loaderService.show('Copying coding question');
    this.subscriptions.push(
      this.dashboardService.CopyQuestion(question).subscribe((response) => {
        this.cqsInfo?.questions?.unshift(response);
        this.totalCodingQuestions += 1;
        if (typeof this.cqsInfo.totalQuestions === 'number') {
          this.cqsInfo.totalQuestions += 1;
        }
        this.toastr.success(toasterMessages.questionCQAdded, 'Question added', { timeOut: 2500 });
        this.loaderService.hide();
      }, (err) => {
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
        this.loaderService.hide();
      })
    );
  }

  /** Update Coding question api request */
  private updateCodingQuestion(question, dialogRef) {
    this.loaderService.show('Updating coding question');
    this.subscriptions.push(
      this.dashboardService.updateQuestion(question, question._id).subscribe((response) => {
        const selectedIndex = this.cqsInfo?.questions?.findIndex(x => x._id === question._id);
        if (selectedIndex > -1) {
          this.cqsInfo.questions[selectedIndex] = response;
        }
        this.loaderService.hide();
        if (dialogRef) {
          dialogRef.componentInstance.data.update = true;
          dialogRef.componentInstance.editObj = response;
          dialogRef.componentInstance.isNewLangQuestion = false;
        }
        this.toastr.success(toasterMessages.questionCQUpdated, 'Question updated', { timeOut: 2500 });
      }, err => {
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
        this.loaderService.hide();
      })
    );
  }

  /** creates a copy of the question */
  copyOeq(question) {
    this.copyOpenEndedQuestion({ id: question._id });
  }

  /** Create or Update open ended choice Question */
  createOrUpdateOeq(obj = null) {
    const dialogRef = this.dialog.open(CreateUpdateOeqComponent, {
      width: '700px',
      panelClass: 'create-update-oeq-dialog',
      data: {
        update: obj ? true : false,
        content: obj ? obj : ``,
        difficultyLevel: 'MEDIUM'
      }
    });

    dialogRef.componentInstance.createUpdateOeq.subscribe((question: any) => {
      const mcqReqObj = {
        questionType: 'OPEN_ENDED',
        questionText: question?.questionText,
        answer: question?.answer || '',
        topicRefs: question?.topics,
        difficultyLevel: question?.difficultyLevel,
        hourLimit: question?.hourLimit,
        minLimit: question?.minLimit,
        subTopics: question?.subTopics,
        _id: question?._id
      };
      if (question?.update) {
        this.updateOpenEndedQuestion(mcqReqObj, dialogRef);
      } else {
        this.createOpenEndedQuestion(mcqReqObj, dialogRef);
      }
    });
  }

  /** create open ended question api call*/
  createOpenEndedQuestion(question, dialogRef = null) {
    this.loaderService.show('Adding open ended question');
    this.subscriptions.push(
      this.dashboardService.createQuestion(question).subscribe((response) => {
        this.oeqsInfo?.questions?.unshift(response);
        this.totalOEQuestions += 1;
        this.oeqsInfo.totalQuestions += 1;
        dialogRef?.close();
        this.loaderService.hide();
        this.toastr.success(toasterMessages.questionOEQAdded, 'Question added', { timeOut: 2500 });
      }, (err) => {
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
        this.loaderService.hide();
      })
    );
  }

  /** create copy of open ended question api call*/
  copyOpenEndedQuestion(question) {
    this.loaderService.show('Copying open ended question');
    this.subscriptions.push(
      this.dashboardService.CopyQuestion(question).subscribe((response) => {
        this.oeqsInfo?.questions?.unshift(response);
        this.totalOEQuestions += 1;
        this.oeqsInfo.totalQuestions += 1;
        this.loaderService.hide();
        this.toastr.success(toasterMessages.questionOEQAdded, 'Question added', { timeOut: 2500 });
      }, (err) => {
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
        this.loaderService.hide();
      })
    );
  }

  /** update open ended  question api call*/
  updateOpenEndedQuestion(question, dialogRef) {
    this.loaderService.show('Updating open ended question');
    this.subscriptions.push(
      this.dashboardService.updateQuestion(question, question._id).subscribe((response) => {
        dialogRef.close();
        const selectedIndex = this.oeqsInfo?.questions?.findIndex(x => x._id === question._id);
        this.oeqsInfo.questions[selectedIndex] = response;
        this.loaderService.hide();
        this.toastr.success(toasterMessages.questionOEQUpdated, 'Question updated', { timeOut: 2500 });
      }, (err) => {
        this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
        this.loaderService.hide();
      })
    );
  }

  tabChanged(tab): void {
    this.activeTabIndex = tab.index;
  }

  toggleExpandAll() {
    this.allExpanded = !this.allExpanded;
    let questionsArray;
    if (this.activeTabIndex === 0) {
      questionsArray = this.mcqsInfo.questions;
    } else if (this.activeTabIndex === 1) {
      questionsArray = this.oeqsInfo.questions;
    } else if (this.activeTabIndex === 2) {
      questionsArray = this.cqsInfo.questions;
    }
    if (!questionsArray) { return; }

    if (this.activeTabIndex === 2 && this.cqsList) {
      // First toggle flags so *ngIf areas render
      questionsArray.forEach(q => q.expanded = this.allExpanded);

      if (this.allExpanded) {
        // Wait for DOM to paint, then initialize each editor
        setTimeout(() => {
          questionsArray.forEach((q, idx) => this.cqsList.onPanelOpen(q, idx));
        }, 0);
      } else {
        // Collapse: cleanly close all panels
        questionsArray.forEach(q => this.cqsList.onPanelClose(q));
      }
      return;
    }

    // Default behavior for other tabs
    questionsArray.forEach(q => q.expanded = this.allExpanded);
  }

  onMcqScroll() {
    if (this.activeTabIndex === 0) {
      this.mcqPageNo++;
      if (this.totalMcqQuestions > this.mcqsInfo?.questions?.length) {
        this.getMCQuestions(this.mcqPageNo, 'MULTIPLE_CHOICE', this.filterValues);
      }
    }
  }

  onOeqScroll() {
    if (this.activeTabIndex === 1) {
      this.oePageNo++;
      if (this.totalOEQuestions > this.oeqsInfo?.questions?.length) {
        this.getOEQuestions(this.oePageNo, 'OPEN_ENDED', this.filterValues);
      }
    }
  }

  onCqScroll() {
    if (this.activeTabIndex === 2) {
      this.cqPageNo++;
      if (this.totalCodingQuestions > this.cqsInfo?.questions?.length) {
        this.getCodingQuestions(this.cqPageNo, 'CODING', this.filterValues);
      }
    }
  }

  generatedRemoveWithUndoClick(question): void {
    question.deleted = false;
  }

  questionDeselected(question) {
    // Use originSection to determine where to add the question back
    if (!question.originSection) {
      // Default to generated if not set
      question.originSection = 'GENERATED';
    }
    if (question.type === 'MULTIPLE_CHOICE' && this.mcqsInfo) {
      if (question.originSection === 'PREVIOUSLYGENERATED') {
        // Add to previously generated (for now, same as generated in this UI)
        this.mcqsInfo.questions.unshift(question);
      } else {
        this.mcqsInfo.questions.unshift(question);
      }
      this.mcqsInfo.totalQuestions += 1;
      this.totalMcqQuestions += 1;
    } else if (question.type === 'OPEN_ENDED' && this.oeqsInfo) {
      if (question.originSection === 'PREVIOUSLYGENERATED') {
        this.oeqsInfo.questions.unshift(question);
      } else {
        this.oeqsInfo.questions.unshift(question);
      }
      this.oeqsInfo.totalQuestions += 1;
      this.totalOEQuestions += 1;
    } else if (question.type === 'CODING' && this.cqsInfo) {
      if (question.originSection === 'PREVIOUSLYGENERATED') {
        this.cqsInfo.questions.unshift(question);
      } else {
        this.cqsInfo.questions.unshift(question);
      }
      this.cqsInfo.totalQuestions += 1;
      this.totalCodingQuestions += 1;
    }
  }

  onMcqCountChanged(newCount: number) {
    this.mcqsInfo.totalQuestions = newCount;
  }

  onOeqCountChanged(newCount: number) {
    this.oeqsInfo.totalQuestions = newCount;
    this.totalOEQuestions = newCount;
  }

  onMcqBulkAction(action: string) {
    if (action === 'delete') {
      this.showMcqBulkCheckboxes = true;
    }
    // Add more actions as needed
  }
  onMcqBulkActionDone() {
    this.showMcqBulkCheckboxes = false;
    this.mcqsInfo?.questions?.forEach(q => q._selected = false);
  }
  onOeqBulkAction(action: string) {
    if (action === 'delete') {
      this.showOeqBulkCheckboxes = true;
    }
    // Add more actions as needed
  }
  onOeqBulkActionDone() {
    this.showOeqBulkCheckboxes = false;
    this.oeqsInfo?.questions?.forEach(q => q._selected = false);
  }
  onCqBulkAction(action: string) {
    if (action === 'delete') {
      this.showCqBulkCheckboxes = true;
    }
    // Add more actions as needed
  }
  onCqBulkActionDone() {
    this.showCqBulkCheckboxes = false;
    this.cqsInfo?.questions?.forEach(q => q._selected = false);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sb => sb.unsubscribe());
  }

  get hasSelectedMcqs(): boolean {
    return this.mcqsInfo?.questions?.some(q => q._selected);
  }
  get hasSelectedOeqs(): boolean {
    return this.oeqsInfo?.questions?.some(q => q._selected);
  }
  get hasSelectedCqs(): boolean {
    return this.cqsInfo?.questions?.some(q => q._selected) || false;
  }

  get selectedMcqCount(): number {
    return this.mcqsInfo?.questions?.filter(q => q._selected).length || 0;
  }
  get selectedOeqCount(): number {
    return this.oeqsInfo?.questions?.filter(q => q._selected).length || 0;
  }
  get selectedCqCount(): number {
    return this.cqsInfo?.questions?.filter(q => q._selected).length || 0;
  }

  onMcqEmptyStateAction(action: any): void {
    if (action.action === 'create') {
      this.createOrUpdateMcq();
    }
  }

  onOeqEmptyStateAction(action: any): void {
    if (action.action === 'create') {
      this.createOrUpdateOeq();
    }
  }

  onCqEmptyStateAction(action: any): void {
    if (action.action === 'create') {
      this.createOrUpdateCQ();
    }
  }

}

